import React, { useState } from 'react';
import { FaTimes, FaFileUpload, FaRobot, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const EXTRACT_API_URL = 'https://m1pcfa6j22.execute-api.us-east-1.amazonaws.com/v1';
const API_KEY = process.env.REACT_APP_API_KEY;

interface AIContractFillModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (result: any) => void;
}

interface PresignedUrlResponse {
    request_id: string;
    pdf_upload_url: string;
    excel_upload_url: string;
    pdf_key: string;
    excel_key: string;
    expires_in: number;
}

const AIContractFillModal: React.FC<AIContractFillModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [contractFile, setContractFile] = useState<File | null>(null);
    const [spreadsheetFile, setSpreadsheetFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStep, setUploadStep] = useState('');

    const handleContractFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        console.log('Arquivo selecionado:', file);
        if (file) {
            console.log('Nome:', file.name, 'Tipo:', file.type);
            setContractFile(file);
        }
    };

    const handleSpreadsheetFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ];
            if (!validTypes.includes(file.type)) {
                toast.error('Por favor, selecione um arquivo XLS ou XLSX');
                return;
            }
            setSpreadsheetFile(file);
        }
    };

    const handleSubmit = async () => {
        // Cache desabilitado - descomentar para habilitar
        // const savedResult = localStorage.getItem('ai_contract_result');
        // if (savedResult) {
        //     console.log('Usando resultado salvo do localStorage');
        //     const result = JSON.parse(savedResult);
        //     onSubmit(result);
        //     onClose();
        //     return;
        // }

        if (!contractFile || !spreadsheetFile) {
            toast.error('Por favor, selecione ambos os arquivos');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Passo 1: Obter presigned URLs
            setUploadStep('Obtendo URLs de upload...');
            setUploadProgress(10);
            
            const presignedResponse = await fetch(`${EXTRACT_API_URL}/extract/upload-url`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json'
                }
            });

            if (!presignedResponse.ok) {
                throw new Error('Erro ao obter URLs de upload');
            }

            const presignedData: PresignedUrlResponse = await presignedResponse.json();
            const { request_id, pdf_upload_url, excel_upload_url } = presignedData;

            setUploadProgress(30);

            // Passo 2: Upload do PDF
            setUploadStep('Enviando contrato PDF...');
            setUploadProgress(40);
            
            const pdfUploadResponse = await fetch(pdf_upload_url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/pdf'
                },
                body: contractFile
            });

            if (!pdfUploadResponse.ok) {
                throw new Error('Erro ao enviar arquivo PDF');
            }

            setUploadProgress(70);

            // Passo 3: Upload do Excel
            setUploadStep('Enviando planilha Excel...');
            setUploadProgress(80);
            
            const excelUploadResponse = await fetch(excel_upload_url, {
                method: 'PUT',
                headers: {
                    'Content-Type': spreadsheetFile.type
                },
                body: spreadsheetFile
            });

            if (!excelUploadResponse.ok) {
                throw new Error('Erro ao enviar arquivo Excel');
            }

            setUploadProgress(100);
            setUploadStep('Concluído!');

            // Passo 4: Iniciar processamento
            setUploadStep('Iniciando processamento...');
            setUploadProgress(85);

            const processResponse = await fetch(`${EXTRACT_API_URL}/extract/process-files`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    request_id,
                    pdf_filename: contractFile.name,
                    excel_filename: spreadsheetFile.name
                })
            });

            if (!processResponse.ok) {
                throw new Error('Erro ao iniciar processamento');
            }

            setUploadProgress(90);

            // Passo 5: Aguardar resultado
            setUploadStep('Aguardando processamento...');
            const result = await pollForResult(request_id);

            setUploadProgress(100);
            setUploadStep('Processamento concluído!');
            
            toast.success('Contrato processado com sucesso!');
            
            setTimeout(() => {
                onSubmit(result);
                handleClose();
            }, 500);

        } catch (error) {
            console.error('Erro no upload:', error);
            toast.error(error instanceof Error ? error.message : 'Erro ao processar arquivos');
            setUploadProgress(0);
            setUploadStep('');
        } finally {
            setIsUploading(false);
        }
    };

    const pollForResult = async (requestId: string, maxAttempts = 120): Promise<any> => {
        let attempt = 0;
        
        while (attempt < maxAttempts) {
            attempt++;
            setUploadStep(`Processando contrato... (${attempt}s)`);
            
            const resultResponse = await fetch(`${EXTRACT_API_URL}/extract/result/${requestId}`, {
                headers: {
                    'x-api-key': API_KEY || ''
                }
            });

            if (!resultResponse.ok) {
                throw new Error('Erro ao consultar resultado');
            }

            const resultData = await resultResponse.json();
            
            // Parse body se vier como string
            const parsedBody = typeof resultData.body === 'string' 
                ? JSON.parse(resultData.body) 
                : resultData.body;
            
            const status = parsedBody.status;

            console.log(`Tentativa ${attempt}: Status = ${status}`);

            if (status === 'COMPLETED') {
                // Cache desabilitado - descomentar para habilitar
                // localStorage.setItem('ai_contract_result', JSON.stringify(parsedBody.result));
                // console.log('Resultado salvo no localStorage');
                return parsedBody.result;
            } else if (status === 'FAILED') {
                throw new Error(parsedBody.error || 'Processamento falhou');
            }
            
            await new Promise(resolve => setTimeout(resolve, 5000)); // 5 segundos
        }

        throw new Error('Timeout: processamento ainda em andamento');
    };

    const handleClose = () => {
        if (!isUploading) {
            setContractFile(null);
            setSpreadsheetFile(null);
            setUploadProgress(0);
            setUploadStep('');
            onClose();
        }
    };

    const clearSavedResult = () => {
        localStorage.removeItem('ai_contract_result');
        toast.info('Resultado salvo removido');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <FaRobot className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Preencher contrato com IA
                    </h3>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <FaTimes className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="space-y-6">
                    {/* Indicador de cache - oculto quando cache desabilitado */}
                    {/* {localStorage.getItem('ai_contract_result') && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                            <p className="text-sm text-green-700 dark:text-green-300">
                                ✓ Resultado em cache - Clique em "Processar" para usar o último resultado salvo
                            </p>
                        </div>
                    )} */}

                    {/* Barra de Progresso */}
                    {isUploading && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <FaSpinner className="animate-spin text-blue-600 dark:text-blue-400 text-xl" />
                                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                    {uploadStep}
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                <div
                                    className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                            <div className="text-right mt-1">
                                <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                                    {uploadProgress}%
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Upload Contrato PDF */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Contrato
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                            <input
                                type="file"
                                id="ai-contract-upload"
                                accept=".pdf,application/pdf"
                                onChange={handleContractFileChange}
                                className="hidden"
                            />
                            <label
                                htmlFor="ai-contract-upload"
                                className="cursor-pointer flex flex-col items-center"
                            >
                                <FaFileUpload className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                    {contractFile ? `✓ ${contractFile.name}` : 'Clique para selecionar arquivo PDF'}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Formato: PDF
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Upload Planilha */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Planilha de dados
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                            <input
                                type="file"
                                id="spreadsheet-upload"
                                accept=".xls,.xlsx"
                                onChange={handleSpreadsheetFileChange}
                                className="hidden"
                            />
                            <label
                                htmlFor="spreadsheet-upload"
                                className="cursor-pointer flex flex-col items-center"
                            >
                                <FaFileUpload className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {spreadsheetFile ? spreadsheetFile.name : 'Clique para selecionar arquivo Excel'}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Formato: XLS ou XLSX
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {/* Botão limpar cache - oculto quando cache desabilitado */}
                    {/* <button
                        onClick={clearSavedResult}
                        className="px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    >
                        Limpar cache
                    </button> */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isUploading}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={(!contractFile || !spreadsheetFile) || isUploading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isUploading && <FaSpinner className="animate-spin" />}
                            {isUploading ? 'Enviando...' : 'Processar com IA'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIContractFillModal;
